import React, { useEffect, useRef, useState } from 'react';
import { Handle, Position } from 'react-flow-renderer';
import { AppButton } from 'components';
import * as AppPics from 'assets/images';
import { createPortal } from 'react-dom';
import { typesetMath, unwrapMathFromPre } from 'utilities/mathjax.service';

// The app has a global `ul { list-style: none }` reset (see assets/css/style.css)
// intended for nav-style menus. That reset also strips bullet markers from any
// CKEditor-authored <ul>/<ol> lists rendered elsewhere on the page (they only
// render correctly inside the CKEditor iframe, which has its own stylesheet).
// This scopes list styling back on, just for node description previews.
const nodeHtmlListStyles = `
  .mindmap-node-html ul {
    list-style: disc outside;
    padding-left: 20px;
    margin: 0 0 8px;
  }
  .mindmap-node-html ol {
    list-style: decimal outside;
    padding-left: 20px;
    margin: 0 0 8px;
  }
  .mindmap-node-html li {
    display: list-item;
  }
`;

export default function CustomNode({ data }) {
  const mathRef = useRef(null);
  const [showText, setShowText] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const hasDescription =
    data?.description && data.description.replace(/<[^>]*>/g, '').trim() !== '';

  useEffect(() => {
    if (showText && mathRef.current) {
      typesetMath(mathRef.current);
    }
  }, [showText, data?.description]);

  return (
    <div
      className="border rounded shadow-sm bg-white"
      style={{
        display: 'inline-block',
        minWidth: '260px',
        maxWidth: '260px',
        padding: '12px',
        borderWidth: '1.5px',
        position: 'relative',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <h6
          className="fw-bold mb-0"
          style={{
            fontSize: '1.2rem',
            lineHeight: '1.4em',
            height: '4.2em',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word',
          }}
        >
          {data?.title}
        </h6>

        <button
          className="text-info"
          style={{ cursor: 'pointer', flexShrink: 0 }}
          onMouseEnter={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();

            setPopoverPosition({
              top: rect.bottom + window.scrollY + 8,
              left: rect.left + rect.width / 2 + window.scrollX,
            });

            setShowText(true);
          }}
          onMouseLeave={() => setShowText(false)}
        >
          <img src={AppPics.EpisodePng} alt="View description" />
        </button>
      </div>

      {/* Hover Popover */}
      {showText &&
        hasDescription &&
        createPortal(
          <div
            style={{
              position: 'absolute',
              top: popoverPosition.top,
              left: popoverPosition.left,
              transform: 'translateX(-50%)',
              background: '#ead3ed',
              border: '1px solid #ccc',
              borderRadius: 6,
              padding: '10px',
              width: 'max-content',
              minWidth: 260,
              maxWidth: 'min(700px, 90vw)',
              overflowX: 'auto',
              zIndex: 9999,
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            }}
            onMouseEnter={() => setShowText(true)}
            onMouseLeave={() => setShowText(false)}
          >
            <style>{nodeHtmlListStyles}</style>
            <div
              ref={mathRef}
              className="mindmap-node-html"
              dangerouslySetInnerHTML={{
                __html: unwrapMathFromPre(data.description).replace(
                  /<img/g,
                  '<img class="d-block mx-auto img-fluid"'
                ),
              }}
            />
          </div>,
          document.body
        )}

      {/* Edit & Delete Buttons */}
      <div className="d-flex justify-content-end gap-3 mt-4">
        <AppButton
          className="actions-icons mx-2 text-primary"
          onClick={(e) => {
            e.stopPropagation();
            data?.onEdit?.();
          }}
          title="Edit"
        >
          <img src={AppPics.EditSvg} alt="edit" style={{ height: '16px' }} />
        </AppButton>

        <AppButton
          className="actions-icons mx-2 text-danger"
          onClick={(e) => {
            e.stopPropagation();
            data?.onDelete?.();
          }}
          title="Delete"
        >
          <img src={AppPics.DeleteSvg} alt="delete" style={{ height: '16px' }} />
        </AppButton>
      </div>

      {/* Handles */}
      {/* The source handle also doubles as a "+" affordance: click it to add
          a connected node right away, or drag from here and drop on empty
          canvas to create and place a new connected node in one action. */}
      <Handle
        type="source"
        position={Position.Bottom}
        title="Click or drag to add a connected node"
        onClick={(e) => {
          e.stopPropagation();
          data?.onAddChild?.();
        }}
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: '2px solid #fff',
          bottom: -11,
          background: '#7e1f8c',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            pointerEvents: 'none',
            userSelect: 'none',
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          +
        </span>
      </Handle>

      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: 18,
          height: 18,
          top: -11,
          borderRadius: '50%',
          border: '2px solid #fff',
        }}
      />
    </div>
  );
}
